/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import {
  accessAPI,
  Boton,
  Formulario,
  Sidebar,
  TablaFiltrable,
  Titulo,
  tituloBase,
  formatFechaHora
} from "../../shared";
import VisorCorreo from "./VisorCorreo.js";

// Asignar y reintentar responden cuando el asistente terminó, que puede
// tardar bastante más que el timeout por defecto.
const OPCIONES_ASISTENTE = { timeout: 180000 };

/**
 * Bandeja de los correos de la casilla del historial que no se pudieron
 * asignar a un cliente, o cuyo procesamiento falló. Desde acá se asignan a
 * mano: el asistente los incorpora a la conversación elegida, o elige él
 * entre las abiertas del cliente.
 */
export default function CorreosSinAsignar() {
  const toast = useRef(null);
  const menuRef = useRef(null);
  const [correos, setCorreos] = useState(null);
  const [correoMenu, setCorreoMenu] = useState(null);
  const [correoAbierto, setCorreoAbierto] = useState(null);
  const [correoAsignando, setCorreoAsignando] = useState(null);
  const [reprocesando, setReprocesando] = useState(null);

  function cargar() {
    accessAPI(
      "GET",
      "admin/comunicacioncliente/sinasignar",
      null,
      (respuesta) => setCorreos(respuesta),
      () => setCorreos([])
    );
  }

  useEffect(() => {
    document.title = "Correos sin asignar";
    cargar();
    return () => (document.title = tituloBase());
  }, []);

  function reprocesar(correo) {
    setReprocesando(correo.id);
    accessAPI(
      "POST",
      `admin/comunicacioncliente/correo/${correo.id}/reprocesar`,
      null,
      () => {
        setReprocesando(null);
        cargar();
      },
      (respuesta) => {
        setReprocesando(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.error || "No se pudo reprocesar"
        });
      },
      undefined,
      undefined,
      OPCIONES_ASISTENTE
    );
  }

  const itemsMenu = correoMenu
    ? [
        { label: "Ver", icon: "pi pi-envelope", command: () => setCorreoAbierto(correoMenu.id) },
        ...(correoMenu.estado === "ERROR" && correoMenu.clienteId && reprocesando !== correoMenu.id
          ? [{ label: "Reintentar", icon: "pi pi-refresh", command: () => reprocesar(correoMenu) }]
          : []),
        ...(correoMenu.estado !== "RECIBIDO" && reprocesando !== correoMenu.id
          ? [{ label: "Asignar", icon: "pi pi-user", command: () => setCorreoAsignando(correoMenu) }]
          : [])
      ]
    : [];

  const estadoTemplate = (correo) => {
    if (correo.estado === "RECIBIDO" || reprocesando === correo.id) {
      return <Tag value="Procesando" severity="info" />;
    }
    if (correo.posibleSpam) return <Tag value="Posible spam" severity="warning" />;
    if (correo.estado === "ERROR") return <Tag value="Error" severity="danger" />;
    return <Tag value="Sin asignar" severity="secondary" />;
  };

  return (
    <>
      <Toast ref={toast} />
      <Menu model={itemsMenu} popup ref={menuRef} />
      <Titulo>Correos sin asignar</Titulo>

      {correos === null && (
        <Card>
          <Skeleton width="100%" height="3rem" />
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} width="100%" height="3.5rem" css={{ marginTop: "0.3rem" }} />
          ))}
        </Card>
      )}

      {correos !== null && (
        <Card>
          <TablaFiltrable
            value={correos}
            searchFields={["de", "asunto"]}
            nombreArchivo="correos_sin_asignar"
            emptyMessage="No hay correos sin asignar."
            size="small"
          >
            <Column
              header="Recibido"
              body={(c) => formatFechaHora(c.fechaRecepcion)}
              exportValue={(c) => formatFechaHora(c.fechaRecepcion)}
            />
            <Column header="De" field="de" />
            <Column
              header="Asunto"
              body={(c) => c.asunto || "(sin asunto)"}
              exportValue={(c) => c.asunto || ""}
            />
            <Column header="Estado" body={estadoTemplate} />
            <Column
              body={(correo) => (
                <Boton
                  texto
                  negro
                  callback={(e) => {
                    setCorreoMenu(correo);
                    menuRef.current.toggle(e);
                  }}
                >
                  <i className="pi pi-ellipsis-v" />
                </Boton>
              )}
              style={{ width: "60px", textAlign: "center" }}
            />
          </TablaFiltrable>
        </Card>
      )}

      <Sidebar
        visible={!!correoAbierto}
        position="right"
        onHide={() => setCorreoAbierto(null)}
        style={{ width: "min(60rem, 100vw)" }}
      >
        {correoAbierto && <VisorCorreo correoId={correoAbierto} toast={toast} />}
      </Sidebar>

      <Sidebar
        visible={!!correoAsignando}
        position="right"
        onHide={() => setCorreoAsignando(null)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        {correoAsignando && (
          <AsignarCorreo
            correo={correoAsignando}
            toast={toast}
            onAsignado={() => {
              setCorreoAsignando(null);
              cargar();
            }}
          />
        )}
      </Sidebar>
    </>
  );
}

function AsignarCorreo({ correo, toast, onAsignado }) {
  // undefined mientras se busca la sugerencia; null si no hay
  const [sugerido, setSugerido] = useState(undefined);
  const [asignando, setAsignando] = useState(false);

  // Si el remitente ya es contacto de un cliente, se lo propone.
  useEffect(() => {
    accessAPI(
      "GET",
      `admin/comunicacioncliente/correo/${correo.id}/sugerencia`,
      null,
      (respuesta) => setSugerido(respuesta.clienteId || null),
      () => setSugerido(null)
    );
  }, [correo.id]);

  function asignar(formData) {
    setAsignando(true);
    accessAPI(
      "PUT",
      `admin/comunicacioncliente/correo/${correo.id}/asignar`,
      { clienteId: formData.clienteId, conversacionId: formData.conversacionId || null },
      (respuesta) => {
        setAsignando(false);
        toast.current?.show(
          respuesta.estado === "PROCESADO"
            ? { severity: "success", summary: "Asignado", detail: "El correo se incorporó a la conversación" }
            : {
                severity: "warn",
                summary: "Asignado con error",
                detail: respuesta.errorProcesamiento || "El asistente no pudo procesarlo"
              }
        );
        onAsignado();
      },
      (respuesta) => {
        setAsignando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.error || "No se pudo asignar"
        });
      },
      undefined,
      undefined,
      OPCIONES_ASISTENTE
    );
  }

  const config = {
    secciones: [
      {
        campos: [
          {
            nombre: "clienteId",
            tipoitemformularioid: 5,
            etiqueta: "Cliente",
            opciones: "admin/comunicacioncliente/clientes",
            sinOpcionInicial: true,
            respuesta: sugerido,
            requerido: true
          },
          {
            nombre: "conversacionId",
            tipoitemformularioid: 5,
            etiqueta: "Conversación",
            opciones: "admin/comunicacioncliente/opcionesconversacion",
            queryparams: ["clienteId"],
            sinOpcionInicial: true,
            respuesta: 0
          }
        ]
      }
    ],
    botones: [{ etiqueta: "Asignar", callback: asignar, validar: true }]
  };

  return (
    <>
      <Titulo subtitulo={correo.de}>{correo.asunto || "(sin asunto)"}</Titulo>
      {sugerido === undefined ? (
        <div css={{ display: "flex", flexDirection: "column", gap: "1em" }}>
          <Skeleton width="100%" height="3rem" />
          <Skeleton width="100%" height="3rem" />
        </div>
      ) : (
        <Formulario config={config} loader={asignando} />
      )}
    </>
  );
}
